import type { ReactNode } from 'react'
import {
  IconeAvaliacao,
  IconeCamadas,
  IconeCena,
  IconeConcluido,
  IconeCunha,
  IconeEquipamentos,
  IconeFisica,
  IconeGirar,
  IconeInverter,
  IconePerturbar,
  IconeReiniciar,
  IconeRemover,
} from '../icones'
import type { FerramentasModulo } from './tipos'

export interface PropsEstabilizacao {
  painelCenario: () => ReactNode
  painelEquipamentos?: () => ReactNode
  montagemNoInspetor?: boolean
  painelMontagem?: () => ReactNode
  aoAbrirMontagem?: () => void
  painelSuspensao: () => ReactNode
  painelVisualizacao: () => ReactNode
  painelAvaliacao?: () => ReactNode
  quantidadeApoios: number
  progresso: number
  secundariaConcluida: boolean
  suspensaoAnulada: boolean
  aoPerturbar: () => void
  /** A sequência de perturbação ainda está correndo. */
  perturbando?: boolean
  aoLimparApoios: () => void
  apoioSelecionado: {
    id: string
    nome: string
    equipamento: string
    rotacaoGraus: number
    comCunhaAjuste: boolean
    ehLigacao?: boolean
    ehCalco: boolean
    ehBloco?: boolean
    invertido?: boolean
    aoGirar: (graus: number) => void
    aoAlternarCunha: () => void
    aoInverter?: () => void
    aoRemover: () => void
  } | null
  aoLimparSelecao: () => void
}

/**
 * Estabilização: ferramentas na trilha lateral estilo Photoshop.
 * No ribbon superior ficam: Cenário, Suspensão e vítima, Visualização e ações rápidas.
 */
export function ferramentasEstabilizacao(p: PropsEstabilizacao): FerramentasModulo {
  const apoio = p.apoioSelecionado

  return {
    grupos: [
      ...(p.painelMontagem ? [{ id: 'montagem', rotulo: 'Montagem', icone: <IconeEquipamentos />, conteudo: p.painelMontagem, aoAtivar: p.montagemNoInspetor ? p.aoAbrirMontagem : undefined }] : []),
      { id: 'cenario', rotulo: 'Cenário', icone: <IconeCena />, conteudo: p.painelCenario },
      {
        id: 'suspensao',
        rotulo: 'Suspensão e vítima',
        icone: <IconeFisica />,
        conteudo: p.painelSuspensao,
      },
      {
        id: 'visualizacao',
        rotulo: 'Visualização',
        icone: <IconeCamadas />,
        conteudo: p.painelVisualizacao,
      },
    ],
    chips: [],
    acoesRapidas: (
      <>
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--primario"
          onClick={p.aoPerturbar}
          aria-busy={p.perturbando}
          title="Testar estabilidade: sacode as quatro suspensões"
        >
          <IconePerturbar />{p.perturbando ? 'Perturbando…' : 'Perturbar Veículo'}
        </button>
        {p.quantidadeApoios > 0 && (
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            onClick={p.aoLimparApoios}
            title="Remover todos os apoios"
          >
            <IconeReiniciar />Limpar Calços
          </button>
        )}
      </>
    ),
    inspetor: apoio && {
      titulo: apoio.nome,
      subtitulo: 'Apoio posicionado',
      propriedades: [
        { rotulo: 'Equipamento', valor: apoio.equipamento },
        { rotulo: 'Rotação', valor: `${apoio.rotacaoGraus}°` },
        ...(apoio.ehCalco
          ? [
              { rotulo: 'Orientação', valor: apoio.invertido ? 'invertido (face plana)' : 'normal (degraus)' },
              { rotulo: 'Cunha de ajuste', valor: apoio.comCunhaAjuste ? 'ativa' : 'sem cunha' },
            ]
          : []),
      ],
      acoes: (
        <>
          {apoio.ehLigacao ? <button className="btn-acao-cad btn-acao-cad--primario" onClick={p.aoAbrirMontagem}>Editar pontos e tensão</button> : <>
          <div className="inspetor__linha-controle">
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--secundario"
              onClick={() => apoio.aoGirar(apoio.rotacaoGraus - 15)}
              aria-label="Girar 15 graus à esquerda"
            >
              −15°
            </button>
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--secundario"
              onClick={() => apoio.aoGirar(apoio.rotacaoGraus + 15)}
              aria-label="Girar 15 graus à direita"
            >
              +15°
            </button>
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--secundario"
              onClick={() => apoio.aoGirar(apoio.rotacaoGraus + 90)}
              aria-label="Girar 90 graus à direita"
              title="Girar 90° (Tecla R)"
            >
              <IconeGirar />+90°
            </button>
          </div>
          <label className="inspetor__campo">
            <span>Rotação: {apoio.rotacaoGraus}°</span>
            <input
              type="range"
              min="0"
              max="360"
              step="5"
              value={apoio.rotacaoGraus}
              onChange={(evento) => apoio.aoGirar(Number(evento.target.value))}
            />
          </label>
          </>}
          {(apoio.ehCalco || apoio.ehBloco) && (
            <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
              {apoio.ehCalco && (
                <button
                  type="button"
                  className={`btn-acao-cad ${apoio.invertido ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
                  onClick={apoio.aoInverter}
                  aria-pressed={apoio.invertido}
                  title="Inverter orientação do calço escalonado (Tecla I)"
                  style={{ flex: 1 }}
                >
                  <IconeInverter />{apoio.invertido ? 'Invertido (I)' : 'Virar calço (I)'}
                </button>
              )}
              <button
                type="button"
                className={`btn-acao-cad ${apoio.comCunhaAjuste ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
                onClick={apoio.aoAlternarCunha}
                aria-pressed={apoio.comCunhaAjuste}
                style={{ flex: 1 }}
                title={apoio.ehBloco ? 'Cunha de aperto fino sobre o topo do bloco' : 'Cunha de aperto fino no degrau'}
              >
                {apoio.comCunhaAjuste ? <><IconeConcluido />Cunha ativa</> : <><IconeCunha />Adicionar cunha</>}
              </button>
            </div>
          )}
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--perigo"
            onClick={apoio.aoRemover}
            title="Remover apoio selecionado (Tecla Delete)"
          >
            <IconeRemover />Remover apoio (Del)
          </button>
        </>
      ),
      acoesRapidasMobile: (
        <>
          {apoio.ehLigacao ? <button className="dock-mobile__botao-rapido" onClick={p.aoAbrirMontagem} aria-label="Editar ligação"><IconeEquipamentos tamanho={15} /></button> : <button
            type="button"
            className="dock-mobile__botao-rapido"
            onClick={(e) => {
              e.stopPropagation()
              apoio.aoGirar(apoio.rotacaoGraus + 90)
            }}
            title="Girar 90°"
            aria-label="Girar calço 90 graus"
          >
            <IconeGirar tamanho={15} />
            <span style={{ fontSize: '10px', fontWeight: 600, marginLeft: '2px' }}>90°</span>
          </button>}
          {apoio.ehCalco && (
            <button
              type="button"
              className={`dock-mobile__botao-rapido${apoio.invertido ? ' dock-mobile__botao-rapido--ativo' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                apoio.aoInverter?.()
              }}
              title="Inverter calço"
              aria-label="Inverter calço"
            >
              <IconeInverter tamanho={15} />
            </button>
          )}
          <button
            type="button"
            className="dock-mobile__botao-rapido dock-mobile__botao-rapido--perigo"
            onClick={(e) => {
              e.stopPropagation()
              apoio.aoRemover()
            }}
            title="Remover apoio"
            aria-label="Remover apoio"
          >
            <IconeRemover tamanho={15} />
          </button>
        </>
      ),
      aoFechar: p.aoLimparSelecao,
    },
  }
}

